import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  CheckboxDescription,
  EnumHelper,
  FormGroupComponent,
  FormsUtil,
  InputErrorsComponent,
  MbsPopupType,
  MbsSize,
  ModalService,
  ModalSettings,
  NumberComponent,
  RadioComponent,
  RadioDescription,
  SelectComponent
} from 'mbs-ui-kit';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { IncrementalData } from 'mbs-ui-shared@models/backup/schedule-models';
import { MonthNamesEnum } from 'mbs-ui-shared@models/MonthNames.enum';
import { Period, PeriodDisplay } from 'mbs-ui-shared@models/Period.enum';
import { PlanMode } from 'mbs-ui-shared@models/PlanTypes.enum';
import { ImmutabilityConfirmModalComponent } from 'mbs-ui-shared@modules/immutability-modals/immutability-confirm-modal/immutability-confirm-modal.component';
import { combineLatest, noop } from 'rxjs';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import {
  FromTime,
  getDefaultGFSPolicySettings,
  GFSPolicySettingsForm,
  RetentionPolicyStepValue
} from 'wizards/models/retention-policy-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { CalculatedPropertyStorage, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';
import {
  getGFSPolicyFormGroup,
  isKeepBackupForCorrect,
  monthlyMoreYValidator,
  moreThatRetention,
  weeklyMoreMYValidator
} from './NBF/retention-policy-step.utils';
import { RetentionPolicyStorageInfoComponent } from './NBF/storage-info/retention-policy-storage-info.component';

const RetentionPolicyStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => RetentionPolicyStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-retention-policy-step',
  templateUrl: './retention-policy-step.component.html',
  providers: [RetentionPolicyStepValueAccessor],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    CheckboxDescription,
    CommonModule,
    FormGroupComponent,
    InputErrorsComponent,
    NgbTooltipModule,
    NumberComponent,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule,
    SelectComponent,
    I18NextPipe,
    HelpLinkDirective,
    RetentionPolicyStorageInfoComponent
  ]
})
export class RetentionPolicyStepComponent extends StepBase<RetentionPolicyStepValue> implements OnInit, OnChanges {
  ability = inject(AbilityService);
  private cdr = inject(ChangeDetectorRef);
  mainService: RemoteManagementWizardsService;
  modalService = inject(ModalService);
  i18nPipe = inject(I18NextPipe);
  private stepService = inject(WizardStepsService);

  @ViewChild('IntelligentRetentionPayAttention', { static: true, read: TemplateRef })
  IntelligentRetentionPayAttention: TemplateRef<HTMLElement>;
  @ViewChild('confirmNotFullModal', { static: true, read: TemplateRef }) confirmNotFullModal: TemplateRef<HTMLElement>;

  @Input() minPeriod = 0;
  @Input() isHybrid = false;
  @Input() isFullBackup = false;
  @Input() isMonthly = false;
  @Input() showIntelligentRetention = false;
  @Input() incrementalScheduleData: IncrementalData = null;

  public readonly MbsSize = MbsSize;
  public readonly elementsSelector = {
    name: {
      minPeriodAlert: 'min-period-alert',
      notSupportedAlert: 'not-supported-alert',
      notFullAlert: 'not-full-alert',
      nbfBlock: 'nbf-block',
      showGFSBlock: 'show-gfs-block',
      notNBFBlock: 'not-nbf-block',
      notSQLNotFileBlock: 'not-sql-not-file-block',
      isSQLorFileBlock: 'is-sql-or-file-block',
      fromTimeSelectedBlock: 'from-time-selected-block',
      alwaysKeepLastVersionBlock: 'always-keep-last-version-block',
      daysAgoAlert: 'days-ago-alert',
      notUnixBlock: 'not-unix-block',
      fileTypeNotHybrid: 'file-type-not-hybrid',
      fileTypeNotHybridNotUnix: 'file-type-not-hybrid-not-unix',
      deleteIfLocallyDeletedAlert: 'delete-if-locally-deleted-alert',
      cbfHybridBlock: 'cbf-hybrid-block',
      hybridNotFileBlock: 'hybrid-not-file-block',
      hybridFileOrSQLBlock: 'hybrid-file-or-sql-block',
      hybridFromTimeSelectedBlock: 'hybrid-from-time-selected-block',
      hybridFromTimeSelectedBlockLabel: 'hybrid-always-keep-last-version-block-label',
      hybridAlwaysKeepLastVersionBlock: 'hybrid-always-keep-last-version-block',
      hybridNotUnixBlock: 'hybrid-not-unix-block'
    }
  };
  private readonly backupDate = 'backup date';

  public storage: Partial<CalculatedPropertyStorage> = {};
  public disableIntelligentRetention = false;
  public periodEnumSelectArray = EnumHelper.EnumToSelectIndexesArray(Period, PeriodDisplay);
  public monthNames = EnumHelper.EnumToSelectIndexesArray(MonthNamesEnum);
  public fromTimeSelectedItems = [
    { value: 0, label: 'modification date' },
    { value: 1, label: this.backupDate }
  ];
  public needShowAlert = false;
  public confirmNotFullBackup = false;
  public correctlyVersionForImmutability = false;
  public isFFIMode = false;

  public keepBackupFFIAlert: string;
  public keepBackupStorageObjectLockAlert: string;
  public deleteFilesLabelAlert = [...this.i18nPipe.transform('wizards:delete_files_label_alert', { returnObjects: true })];
  public gfsLabelDesc = [...this.i18nPipe.transform('wizards:gfs_label_desc', { returnObjects: true })];
  public retentionPolicyMinPeriodAlert = [
    ...this.i18nPipe.transform(
      this.isFFIMode ? 'wizards:retention_policy_min_period_alert_ffi' : 'wizards:retention_policy_min_period_alert',
      {
        returnObjects: true,
        minPeriod: this.minPeriod
      }
    )
  ];

  private deleteVersionsOlderThanCountControl: AbstractControl;
  private deleteVersionsOlderThanPeriodControl: AbstractControl;
  private isShowedModal = false;
  private destroyed = false;

  public get showImmutability(): boolean {
    return this.storage.canShowImmutabilityByStorage && !this.isFFIMode;
  }

  public get showGFS(): boolean {
    return !this.isFFIMode;
  }

  get isShowAlert(): boolean {
    const GFSControls = this.GFSPolicySettingsCtrl.controls;
    const week = GFSControls.weekly && GFSControls.weekly.controls.enabled.value;
    return (
      GFSControls.enabled.value &&
      ((this.isFullBackup && this.isMonthly && week) ||
        (!this.isFullBackup &&
          (week ||
            (GFSControls.monthly && GFSControls.monthly.controls.enabled.value) ||
            (GFSControls.yearly && GFSControls.yearly.controls.enabled.value))))
    );
  }

  get isShowWarning(): boolean {
    return (
      this.stepForm &&
      !this.stepForm.get('RetentionUseDefaultSettings').value &&
      this.stepForm.get('deleteVersionsOlderThan').value &&
      this.stepForm.get('fromTimeSelected').value === FromTime['modification date'] &&
      (!this.isBackupPlan || !this.stepForm.get('alwaysKeepLastVersion').value)
    );
  }

  get daysCount(): number {
    const period = this.stepForm.get('deleteVersionsOlderThanPeriod').value;
    const count = this.stepForm.get('deleteVersionsOlderThanCount').value;
    const daysInPeriods = {
      [Period.day]: 1,
      [Period.week]: 7,
      [Period.month]: 30,
      [Period.year]: 365
    };

    return count * (daysInPeriods[period] ?? 1);
  }

  get needShowMinPeriodAlert(): boolean {
    if (!this.minPeriod || !this.isNBF) return false;

    if (this.stepForm.get('RetentionUseDefaultSettings').value || isNil(this.isFullBackup)) {
      return false;
    }

    const count = this.stepForm.get('deleteVersionsOlderThanCount').value;
    const period = this.stepForm.get('deleteVersionsOlderThanPeriod').value;
    let reason: boolean;

    switch (period) {
      case Period.year: {
        reason = false;
        break;
      }
      case Period.day: {
        reason = count < this.minPeriod;
        break;
      }
      case Period.week: {
        reason = count * 7 < this.minPeriod;
        break;
      }
      default: {
        reason = count * 30 < this.minPeriod;
      }
    }

    return reason;
  }

  get GFSPolicySettingsCtrl(): FormGroup<GFSPolicySettingsForm> {
    return <FormGroup<GFSPolicySettingsForm>>this.stepForm?.get('GFSPolicySettings');
  }

  get HybridRetentionPolicyCtrl(): FormGroup {
    return <FormGroup>this.stepForm?.get('HybridRetentionPolicy');
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.correctlyVersionForImmutability = true;
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      RetentionUseDefaultSettings: new FormControl(!this.isFFIMode),
      keepNumberOfVersions: new FormControl(false),
      RetentionNumberOfVersions: new FormControl(1, Validators.min(1)),
      deleteVersionsOlderThan: new FormControl(false), // Only fo CBF
      deleteVersionsOlderThanCount: new FormControl(1, [Validators.min(1)]),
      deleteVersionsOlderThanPeriod: new FormControl(),
      fromTimeSelected: new FormControl(0),
      alwaysKeepLastVersion: new FormControl(false),
      delayPurgeForEnabled: new FormControl(false),
      delayPurgeFor: new FormControl(1, Validators.min(1)),
      delayPurgeForSelect: new FormControl(0),
      deleteIfLocallyDeleted: new FormControl(false),
      deleteAfter: new FormControl(1, Validators.min(1)),
      doNotShowWarning: new FormControl(false),
      intelligentRetention: new FormControl(false),
      GFSPolicySettings: getGFSPolicyFormGroup(this.weeklyMoreMonthlyValidator.bind(this), this.monthlyMoreMonthlyValidator.bind(this)),
      HybridRetentionPolicy: new UntypedFormGroup({
        RetentionUseDefaultSettings: new FormControl(true),
        keepNumberOfVersions: new FormControl(false),
        RetentionNumberOfVersions: new FormControl(1, Validators.min(1)),
        deleteVersionsOlderThan: new FormControl(false),
        deleteVersionsOlderThanCount: new FormControl(1),
        deleteVersionsOlderThanPeriod: new FormControl(),
        fromTimeSelected: new FormControl(0),
        alwaysKeepLastVersion: new FormControl(false),
        delayPurgeForEnabled: new FormControl(false),
        delayPurgeFor: new FormControl(1, Validators.min(1)),
        delayPurgeForSelect: new FormControl(0)
      })
    });

    this.initFormEvents();

    if (this.isUnix && !this.isNetBuild && this.isNBF) this.GFSPolicySettingsCtrl?.disable();
  }

  protected initFormEvents(): void {
    this.deleteVersionsOlderThanCountControl = this.stepForm.get('deleteVersionsOlderThanCount');
    this.deleteVersionsOlderThanPeriodControl = this.stepForm.get('deleteVersionsOlderThanPeriod');

    combineLatest([
      this.deleteVersionsOlderThanCountControl.valueChanges,
      this.deleteVersionsOlderThanPeriodControl.valueChanges
    ]).subscribe({
      next: () => {
        this.keepBackupForStorageObjectLockValidator();
        this.keepBackupForValidator();
      }
    });

    super.initFormEvents();
  }

  private initEvents(): void {
    this.stepService
      .pipeProperty('storage')
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        if (this.storage.canShowImmutabilityByStorage !== data.canShowImmutabilityByStorage) {
          this.enableImmutability();
        }

        if (this.storage.immutabilityPeriodDays !== data.immutabilityPeriodDays) {
          this.keepBackupForStorageObjectLockValidator();
        }

        this.storage = data;
      });

    this.stepService
      .pipeProperty('schedule')
      .pipe(untilDestroyed(this))
      .subscribe((schedule) => {
        this.isFFIMode = schedule.isFFI || schedule.isSimpleMode; // compatible with new Schedule step

        this.enableImmutability();
      });
  }

  onStepFormChange(value: RetentionPolicyStepValue): void {
    const invalidKeepBackupFor = this.isFFIMode && (this.keepBackupFFIAlert || this.keepBackupStorageObjectLockAlert);

    if (this.stepForm.dirty || this.stepForm.touched) {
      const data = value.GFSPolicySettings;
      const gfsEnabled = data && data.enabled;
      const oldValid = this.value.valid;
      this.needShowAlert = gfsEnabled && !data.weekly?.enabled && !data.monthly?.enabled && !data.yearly?.enabled;
      this.value = {
        ...value,
        valid:
          this.stepForm.valid &&
          !this.needShowAlert &&
          !invalidKeepBackupFor &&
          (!this.isNBF || this.isFullBackup || this.isFFIMode || this.confirmNotFullBackup)
      };

      if (oldValid !== this.value?.valid) {
        this.isValid.emit(this.value?.valid);
      }

      return;
    }

    if (this.value) this.value.valid = this.value.valid && !invalidKeepBackupFor;
  }

  keepBackupForStorageObjectLockValidator(): void {
    const final = () => {
      this.removeError([this.deleteVersionsOlderThanCountControl, this.deleteVersionsOlderThanPeriodControl], 'storageObjectLock');

      return void delete this.keepBackupStorageObjectLockAlert;
    };

    if (!this.storage.immutabilityPeriodDays) {
      final();
    }

    const selected: { count: number; period: Period } = {
      count: this.deleteVersionsOlderThanCountControl.value,
      period: this.deleteVersionsOlderThanPeriodControl.value
    };

    const hasError = moreThatRetention({ count: this.storage.immutabilityPeriodDays, period: Period.day }, selected, false);

    if (hasError) {
      this.keepBackupStorageObjectLockAlert = this.i18nPipe.transform('wizards:retention.keepBackupStorageObjectLockErrorMessage', {
        count: this.storage.immutabilityPeriodDays,
        period: this.periodEnumSelectArray[Period.day].label.toLocaleString().toLowerCase()
      });

      this.deleteVersionsOlderThanCountControl.setErrors({ storageObjectLock: true });
      this.deleteVersionsOlderThanPeriodControl.setErrors({ storageObjectLock: true });
      this.deleteVersionsOlderThanCountControl.markAsDirty();
      this.deleteVersionsOlderThanPeriodControl.markAsDirty();

      this.value = { ...this.value, valid: false };

      return;
    }

    final();
  }

  /**
   * Depends of Schedule Step data
   * @return {ValidationErrors | null}
   */
  keepBackupForValidator(): void {
    if (!this.isFFIMode || !this.incrementalScheduleData) {
      return void delete this.keepBackupFFIAlert;
    }

    const selected: { count: number; period: Period } = {
      count: this.deleteVersionsOlderThanCountControl.value,
      period: this.deleteVersionsOlderThanPeriodControl.value
    };

    const isKeepBackupForResult = isKeepBackupForCorrect(this.incrementalScheduleData, selected);

    if (!isKeepBackupForResult.error) {
      this.removeError([this.deleteVersionsOlderThanCountControl, this.deleteVersionsOlderThanPeriodControl], 'keepBackupPeriodInvalid');

      return void delete this.keepBackupFFIAlert;
    }

    this.keepBackupFFIAlert = this.i18nPipe.transform('wizards:keep_backup_ffi_error_message', {
      count: isKeepBackupForResult.count,
      period: this.periodEnumSelectArray[isKeepBackupForResult.period].label.toLocaleString().toLowerCase().split('(')[0]
    });

    this.deleteVersionsOlderThanCountControl.setErrors({ keepBackupPeriodInvalid: true });
    this.deleteVersionsOlderThanPeriodControl.setErrors({ keepBackupPeriodInvalid: true });
  }

  monthlyMoreMonthlyValidator(control: AbstractControl): ValidationErrors | null {
    return monthlyMoreYValidator(this.GFSPolicySettingsCtrl, control);
  }

  weeklyMoreMonthlyValidator(control: AbstractControl): ValidationErrors | null {
    return weeklyMoreMYValidator(this.GFSPolicySettingsCtrl, control);
  }

  ngOnChanges(changes: SimpleChanges): void {
    let isFFIMode = this.isFFIMode;
    let needResetFFI = false;

    if (this.stepForm) {
      if (this.isNBF) {
        if (changes.isFullBackup || changes.isFFIMode) {
          this.confirmNotFullBackup = !!this.isFullBackup || !!this.isFFIMode;
          queueMicrotask(() => {
            this.stepForm.markAsDirty();
            this.stepForm.updateValueAndValidity();
            this.cdr.detectChanges();
          });
        }

        if (changes.showImmutability) {
          const gfsControl: FormGroup<GFSPolicySettingsForm> = this.GFSPolicySettingsCtrl;

          if (this.showImmutability && gfsControl.value.enabled) {
            gfsControl.get('enabledImmutability').enable();
          } else {
            gfsControl.get('enabledImmutability').disable();
          }
        }

        if (changes.showIntelligentRetention?.currentValue === false) {
          this.stepForm.get('intelligentRetention').setValue(true);
        }
      } else {
        if (changes.isHybrid && (!this.isRDMode || !isNil(changes.isHybrid.currentValue))) {
          this.updateDataFromHybridFlag();
        }
      }
    }

    if (changes.minPeriod || changes.incrementalScheduleData || changes.isFFIMode) {
      this.retentionPolicyMinPeriodAlert = [
        ...this.i18nPipe.transform(
          this.isFFIMode ? 'wizards:retention_policy_min_period_alert_ffi' : 'wizards:retention_policy_min_period_alert',
          {
            returnObjects: true,
            minPeriod: this.minPeriod
          }
        )
      ];
    }

    if (this.stepForm && changes.isFFIMode) {
      if (changes.isFFIMode?.currentValue) {
        isFFIMode = true;
        this.turnFFIMode(true);
      } else {
        needResetFFI = true;
        this.GFSPolicySettingsCtrl.reset(getDefaultGFSPolicySettings());
      }
    }

    if (needResetFFI || changes.incrementalScheduleData) {
      setTimeout(() => {
        this.keepBackupForPeriodChangeHandler();
        this.turnFFIMode(isFFIMode);
      });
    }
  }

  updateDataFromHybridFlag(): void {
    if (!this.isRDMode && this.isHybrid) {
      return void queueMicrotask(() => {
        this.changeStateDeleteIfLocallyDeleted();
        this.updateHybridFields(this.isHybrid);
      });
    }

    this.changeStateDeleteIfLocallyDeleted();
    this.updateHybridFields(this.isHybrid);
  }

  turnFFIMode(on: boolean) {
    if (on) {
      this.stepForm.get('RetentionUseDefaultSettings').setValue(false);
      this.GFSPolicySettingsCtrl.get('enabled').setValue(false);
      this.enableGFSChangeHandler(false);
      this.doNotPurgeChangeHandler(false);
    }

    if (this.isNBF === null) return;

    this.stepForm.get('deleteVersionsOlderThanCount').markAsTouched();
    this.stepForm.get('deleteVersionsOlderThanPeriod').markAsTouched();
    this.stepForm.markAsDirty();
    this.stepForm.updateValueAndValidity();
  }

  updateForm(value: RetentionPolicyStepValue): void {
    if (this.isRDMode && value.HybridRetentionPolicy && !value.HybridRetentionPolicy.RetentionUseDefaultSettings) {
      delete value.HybridRetentionPolicy;
      this.importedPlanChanged = true;
    }

    this.stepForm.reset(value);
    this.isValid.emit(this.value.valid);

    if (!this.isNBF) {
      this.retentionUseDefaultSettingsChangeHandler(value.RetentionUseDefaultSettings);

      if (this.isBackupPlan) {
        this.fromTimeSelectedChangeHandler(value.fromTimeSelected);
        this.deleteIfLocallyDeletedChangeHandler(value.deleteIfLocallyDeleted);
      }

      this.updateHybridFields(
        this.isBackupPlan && this.isHybrid && !(value.HybridRetentionPolicy === null || value.HybridRetentionPolicy === undefined)
      );
    } else {
      this.doNotPurgeChangeHandler(value.RetentionUseDefaultSettings);
      this.enableGFSChangeHandler(value.GFSPolicySettings && value.GFSPolicySettings.enabled);
    }

    this.updateHybridFields(this.isHybrid && !(value.HybridRetentionPolicy === null || value.HybridRetentionPolicy === undefined));

    // Temporary only for old version of Retention Policy step MBS-21380
    {
      this.disableIntelligentRetention = this.stepForm.get('intelligentRetention').value;
    }
  }

  forceValid(data: ForceValidType = null): void {
    const GFS: FormGroup<GFSPolicySettingsForm> = this.GFSPolicySettingsCtrl;
    const week = GFS && GFS.controls.weekly && GFS.controls.weekly.controls.storePeriod && GFS.controls.weekly.controls.storePeriod.invalid;
    const month =
      GFS && GFS.controls.monthly && GFS.controls.monthly.controls.storePeriod && GFS.controls.monthly.controls.storePeriod.invalid;

    if (this.isFFIMode && this.stepForm.get('deleteVersionsOlderThanCount').invalid) {
      return void FormsUtil.triggerValidation(this.stepForm);
    }

    if (!this.isShowedModal) {
      if (!week && !month && this.needShowAlert) {
        this.isShowedModal = true;
        this.showGFSAlert(data.isNext, data.isSave);

        return;
      } else {
        FormsUtil.triggerValidation(this.stepForm);
        this.resetValidStateForValidFields();
      }

      if (this.isNBF && !this.isFullBackup && !this.isFFIMode && !this.confirmNotFullBackup && (data.isNext || data.isSave)) {
        this.isShowedModal = true;
        this.showConfirmNotFull(data.isNext, data.isSave);
      }
    }
  }

  keepBackupForPeriodChangeHandler(): void {
    this.stepForm.get('deleteVersionsOlderThanCount').updateValueAndValidity();
  }

  monthlyStorePeriodChangeHandler(): void {
    const weekly = this.GFSPolicySettingsCtrl.get('weekly');

    if (weekly && weekly.value && weekly.value.enabled) {
      weekly.get('storePeriod').updateValueAndValidity();
    }
  }

  yearlyStorePeriodChangeHandler(): void {
    const weekly = this.GFSPolicySettingsCtrl.get('weekly');
    const monthly = this.GFSPolicySettingsCtrl.get('monthly');

    if (weekly && weekly.value && weekly.value.enabled) {
      weekly.get('storePeriod').updateValueAndValidity();
    }
    if (monthly && monthly.value && monthly.value.enabled) {
      monthly.get('storePeriod').updateValueAndValidity();
    }
  }

  changeStateDeleteIfLocallyDeleted(): void {
    const action = this.isHybrid ? 'disable' : 'enable';

    ['deleteIfLocallyDeleted', 'deleteAfter', 'doNotShowWarning'].forEach((key) => this.stepForm.get(key)[action]());
  }

  updateHybridFields(isHybrid: boolean): void {
    const hybridFields = this.stepForm.get('HybridRetentionPolicy');

    if (isHybrid) {
      hybridFields.enable();
      this.setHybridDefaultValue(hybridFields);
    } else hybridFields.disable();

    this.retentionUseDefaultSettingsChangeHandler(!!hybridFields.get('RetentionUseDefaultSettings').value, true);

    this.fromTimeSelectedChangeHandler(hybridFields.get('fromTimeSelected').value, true);
  }

  setHybridDefaultValue(hybridFields): void {
    const keys = Object.keys(hybridFields.value);

    keys.forEach((key) => {
      const control = hybridFields.get(key);

      if (control.value === null || control.value === undefined) {
        if (key === 'fromTimeSelected' || key === 'delayPurgeForSelect') control.setValue(0);
        else if (key === 'deleteVersionsOlderThanCount') control.setValue(1);
        else if (key === 'deleteVersionsOlderThanPeriod') control.setValue(3);
        else if (key === 'delayPurgeFor' || key === 'RetentionNumberOfVersions') control.setValue(3);
        else control.setValue(false);
      }
    });
  }

  doNotPurgeChangeHandler(value: boolean): void {
    const action = value === false ? 'enable' : 'disable';

    if (this.isNBF && value === false) this.confirmNotFullBackup = value || this.isFullBackup || this.isFFIMode;

    ['deleteVersionsOlderThanCount', 'deleteVersionsOlderThanPeriod'].forEach((key) => this.stepForm.get(key)[action]());
  }

  retentionUseDefaultSettingsChangeHandler(event: boolean, isHybrid = false): void {
    const formGroup = isHybrid ? this.stepForm.get('HybridRetentionPolicy') : this.stepForm;
    const keepNumberOfVersions = formGroup.get('keepNumberOfVersions');
    const deleteVersionsOlderThan = formGroup.get('deleteVersionsOlderThan');
    const delayPurgeForEnabled = formGroup.get('delayPurgeForEnabled');
    const needEnable = isHybrid ? event : !event;

    if (needEnable) {
      keepNumberOfVersions.enable();
      deleteVersionsOlderThan.enable();

      if (this.isBackupPlan) delayPurgeForEnabled.enable();
    } else {
      keepNumberOfVersions.disable();
      deleteVersionsOlderThan.disable();

      if (this.isBackupPlan) delayPurgeForEnabled.disable();
    }

    this.keepNumberOfVersionsChangeHandler(needEnable && !!keepNumberOfVersions.value, isHybrid);
    this.deleteVersionsOlderThanChangeHandler(needEnable && !!deleteVersionsOlderThan.value, isHybrid);

    if (this.isBackupPlan) this.delayPurgeForEnabledChangeHandler(needEnable && !!delayPurgeForEnabled.value, isHybrid);
  }

  keepNumberOfVersionsChangeHandler(event: boolean, isHybrid = false): void {
    let keepNumberOfVersions = null;
    let RetentionNumberOfVersions = null;
    let deleteVersionsOlderThan = null;
    let delayPurgeForEnabled = null;

    if (!isHybrid) {
      keepNumberOfVersions = this.stepForm.get('keepNumberOfVersions');
      RetentionNumberOfVersions = this.stepForm.get('RetentionNumberOfVersions');
      deleteVersionsOlderThan = this.stepForm.get('deleteVersionsOlderThan');
      delayPurgeForEnabled = this.stepForm.get('delayPurgeForEnabled');
    } else {
      const hybridFields = this.stepForm.get('HybridRetentionPolicy');
      keepNumberOfVersions = hybridFields.get('keepNumberOfVersions');
      RetentionNumberOfVersions = hybridFields.get('RetentionNumberOfVersions');
      deleteVersionsOlderThan = hybridFields.get('deleteVersionsOlderThan');
      delayPurgeForEnabled = hybridFields.get('delayPurgeForEnabled');
    }

    if (event && keepNumberOfVersions.enabled) {
      RetentionNumberOfVersions.enable();
      delayPurgeForEnabled.enable();
      this.delayPurgeForEnabledChangeHandler(delayPurgeForEnabled.value, isHybrid);
    } else {
      RetentionNumberOfVersions.disable();

      if (!deleteVersionsOlderThan.value) {
        delayPurgeForEnabled.disable();
        this.delayPurgeForEnabledChangeHandler(event, isHybrid);
      }
    }
  }

  deleteVersionsOlderThanChangeHandler(event: boolean, isHybrid = false): void {
    let deleteVersionsOlderThan = null;
    let deleteVersionsOlderThanCount = null;
    let deleteVersionsOlderThanPeriod = null;
    let fromTimeSelected = null;
    let alwaysKeepLastVersion = null;
    let delayPurgeForEnabled = null;
    let keepNumberOfVersions = null;

    if (!isHybrid) {
      deleteVersionsOlderThan = this.stepForm.get('deleteVersionsOlderThan');
      deleteVersionsOlderThanCount = this.stepForm.get('deleteVersionsOlderThanCount');
      deleteVersionsOlderThanPeriod = this.stepForm.get('deleteVersionsOlderThanPeriod');
      fromTimeSelected = this.stepForm.get('fromTimeSelected');
      alwaysKeepLastVersion = this.stepForm.get('alwaysKeepLastVersion');
      delayPurgeForEnabled = this.stepForm.get('delayPurgeForEnabled');
      keepNumberOfVersions = this.stepForm.get('keepNumberOfVersions');
    } else {
      const hybridFields = this.stepForm.get('HybridRetentionPolicy');
      deleteVersionsOlderThan = hybridFields.get('deleteVersionsOlderThan');
      deleteVersionsOlderThanCount = hybridFields.get('deleteVersionsOlderThanCount');
      deleteVersionsOlderThanPeriod = hybridFields.get('deleteVersionsOlderThanPeriod');
      fromTimeSelected = hybridFields.get('fromTimeSelected');
      alwaysKeepLastVersion = hybridFields.get('alwaysKeepLastVersion');
      delayPurgeForEnabled = hybridFields.get('delayPurgeForEnabled');
      keepNumberOfVersions = hybridFields.get('keepNumberOfVersions');
    }

    if (event && deleteVersionsOlderThan.enabled) {
      deleteVersionsOlderThanCount.enable();
      deleteVersionsOlderThanPeriod.enable();

      if (fromTimeSelected.value !== FromTime[this.backupDate] && !this.isSQLPlan) alwaysKeepLastVersion.enable();

      if (this.isBackupPlan || (this.isSQLPlan && !isHybrid)) {
        fromTimeSelected.enable();

        if (!this.isSQLPlan) {
          delayPurgeForEnabled.enable();
          this.delayPurgeForEnabledChangeHandler(delayPurgeForEnabled.value, isHybrid);
        }
      }
    } else {
      deleteVersionsOlderThanCount.disable();
      deleteVersionsOlderThanPeriod.disable();
      alwaysKeepLastVersion.disable();

      if (this.isBackupPlan || this.isSQLPlan) {
        fromTimeSelected.disable();

        if (this.isSQLPlan || !keepNumberOfVersions.value) {
          delayPurgeForEnabled.disable();
          this.delayPurgeForEnabledChangeHandler(event, isHybrid);
        }
      }
    }
  }

  fromTimeSelectedChangeHandler(event, isHybrid = false): void {
    let RetentionUseDefaultSettings = null;
    let alwaysKeepLastVersion = null;

    if (!isHybrid) {
      RetentionUseDefaultSettings = this.stepForm.get('RetentionUseDefaultSettings');
      alwaysKeepLastVersion = this.stepForm.get('alwaysKeepLastVersion');
    } else {
      const hybridFields = this.stepForm.get('HybridRetentionPolicy');
      RetentionUseDefaultSettings = hybridFields.get('RetentionUseDefaultSettings');
      alwaysKeepLastVersion = hybridFields.get('alwaysKeepLastVersion');
    }

    if (!(event === null || event === undefined) && (isHybrid ? RetentionUseDefaultSettings.value : !RetentionUseDefaultSettings.value)) {
      const needEnabled = !(event.value === null || event.value === undefined)
        ? event.value !== FromTime[this.backupDate]
        : event !== FromTime[this.backupDate];

      if (needEnabled && !this.isSQLPlan) alwaysKeepLastVersion.enable();
      else {
        alwaysKeepLastVersion.setValue(true);
        alwaysKeepLastVersion.disable();
      }
    }
  }

  delayPurgeForEnabledChangeHandler(event: boolean, isHybrid = false): void {
    let RetentionUseDefaultSettings = null;
    let delayPurgeFor = null;
    let delayPurgeForSelect = null;

    if (!isHybrid) {
      RetentionUseDefaultSettings = this.stepForm.get('RetentionUseDefaultSettings');
      delayPurgeFor = this.stepForm.get('delayPurgeFor');
      delayPurgeForSelect = this.stepForm.get('delayPurgeForSelect');
    } else {
      const hybridFields = this.stepForm.get('HybridRetentionPolicy');
      RetentionUseDefaultSettings = hybridFields.get('RetentionUseDefaultSettings');
      delayPurgeFor = hybridFields.get('delayPurgeFor');
      delayPurgeForSelect = hybridFields.get('delayPurgeForSelect');
    }

    const validRetention = isHybrid ? RetentionUseDefaultSettings.value : !RetentionUseDefaultSettings.value;

    if (event && validRetention) {
      delayPurgeFor.enable();
      delayPurgeForSelect.enable();
    } else {
      delayPurgeFor.disable();
      delayPurgeForSelect.disable();
    }
  }

  deleteIfLocallyDeletedChangeHandler(event: boolean): void {
    const action = event ? 'enable' : 'disable';

    ['deleteAfter', 'doNotShowWarning'].forEach((key) => this.stepForm.get(key)[action]());
  }

  enableImmutabilityChangeHandler(event: boolean): void {
    if (event) {
      const modalSettings = new ModalSettings();

      modalSettings.responsive = true;
      this.modalService
        .openCustom(ImmutabilityConfirmModalComponent, modalSettings)
        .then(noop)
        .catch((result) => !result && this.GFSPolicySettingsCtrl.get('enabledImmutability').reset(false));
    }
  }

  enableGFSChangeHandler(event: boolean): void {
    const GFSFormGroup = this.GFSPolicySettingsCtrl;

    for (const key of Object.keys(GFSFormGroup.getRawValue())) {
      if (key !== 'enabled') {
        const formGroup = GFSFormGroup.get(key) as UntypedFormGroup;

        if (event) {
          if (key !== 'enabledImmutability') {
            formGroup.enable();
            this.fullBackupsChangeHandler(formGroup.value.enabled, key);
          } else if (this.showImmutability) formGroup.enable();
        } else formGroup.disable();
      }
    }

    if (this.mainService.mode === PlanMode.edit) this.stepForm.markAsTouched();
  }

  fullBackupsChangeHandler(event: boolean, name: string): void {
    const GFSFormGroup = this.GFSPolicySettingsCtrl;

    if (event) {
      if (name === 'yearly') {
        (GFSFormGroup.get(name) as UntypedFormGroup).controls.storePeriod.enable();
        (GFSFormGroup.get(name) as UntypedFormGroup).controls.takeBackupFromMonth.enable();
        this.yearlyStorePeriodChangeHandler();
      } else {
        (GFSFormGroup.get(name) as UntypedFormGroup).controls.storePeriod.enable();
        if (name === 'monthly') this.monthlyStorePeriodChangeHandler();
      }
    } else {
      if (name === 'yearly') {
        (GFSFormGroup.get(name) as UntypedFormGroup).controls.storePeriod.disable();
        (GFSFormGroup.get(name) as UntypedFormGroup).controls.takeBackupFromMonth.disable();
        this.yearlyStorePeriodChangeHandler();
      } else {
        (GFSFormGroup.get(name) as UntypedFormGroup).controls.storePeriod.disable();
        if (name === 'monthly') this.monthlyStorePeriodChangeHandler();
      }
    }

    this.enableImmutability();
  }

  private enableImmutability(): void {
    const GFSFormGroup = this.GFSPolicySettingsCtrl;

    if (this.isNBF && this.showImmutability) {
      const hasGFS = ['weekly', 'monthly', 'yearly'].some((key) => !!GFSFormGroup.get(key).get('enabled').value);

      GFSFormGroup.get('enabledImmutability')[hasGFS ? 'enable' : 'disable']();
    }
  }

  intelligentRetentionChangeHandler(value: boolean) {
    if (value) {
      return;
    }

    this.stepForm.get('intelligentRetention').setValue(true);

    const modalSettings: ModalSettings = {
      header: { title: this.i18nPipe.transform('wizards:intelligent_retention_alert_title', { format: 'title' }) },
      footer: {
        okButton: { text: this.i18nPipe.transform('shared:buttons.disable'), type: 'danger' },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel') }
      }
    };

    this.modalService
      .open(modalSettings, this.IntelligentRetentionPayAttention)
      .catch(noop)
      .then((result) => {
        setTimeout(() => {
          this.stepForm.get('intelligentRetention').setValue(!result);
        });
      });
  }

  changeBackupScheduleClickHandler(): void {
    this.backStep.emit();
  }

  private showGFSAlert(needNextStep: boolean, needSave: boolean): void {
    const modalSettings: ModalSettings = {
      header: { title: this.i18nPipe.transform('wizards:disable_gfs_title', { format: 'title' }) },
      footer: { okButton: { show: false }, cancelButton: { text: 'Close' } }
    };

    this.modalService.open(modalSettings, this.i18nPipe.transform('wizards:disable_gfs_text')).catch(() => {
      this.GFSPolicySettingsCtrl.get('enabled').setValue(false);
      this.enableGFSChangeHandler(false);
      this.stepForm.updateValueAndValidity();
      this.isShowedModal = false;
      if (needNextStep || needSave) this.nextStep.emit(needSave);
    });
  }

  private showConfirmNotFull(needNextStep: boolean, needSave: boolean): void {
    const modalSettings: ModalSettings = {
      header: { title: this.i18nPipe.transform('wizards:not_full_confirm_title', { format: 'title' }) },
      footer: {
        okButton: { text: this.i18nPipe.transform('shared:buttons.ok'), type: 'primary' },
        cancelButton: { show: false }
      }
    };

    this.modalService
      .open(modalSettings, this.confirmNotFullModal)
      .then(() => {
        this.confirmNotFullBackup = true;
        this.stepForm.updateValueAndValidity();
        this.isShowedModal = false;

        if (needNextStep || needSave) this.nextStep.emit(needSave);
      })
      .catch(() => (this.isShowedModal = false));
  }

  protected readonly MbsPopupType = MbsPopupType;
}
